import React, { useLayoutEffect } from 'react';
import { useWindowSize } from '@react-hook/window-size';
import styled from '@emotion/styled';
// ---- external imports ----

import { computeAspectBoxedSize } from '~/lib/aspectRatio';
import { creatScreenResponsivityChangeEvent } from '~/lib/responsiveUtils';
// ---- internal imports ----

import SideBlinders from './SideBlinders';
// ---- local imports ----


function Backdrop(props) {
	const {
		size,
		projection,
		peakrowProjection,
	} = props;

	const [wWidth, wHeight] = useWindowSize();

	useLayoutEffect(() => {
		handleResize({ size, peakrowProjection }, { width: wWidth, height: wHeight });
		fireResponsiveVariablesChangedEvent();
	}, [wWidth, wHeight, size, peakrowProjection]);

	return (
		<Content
			className={props.className}
		>
			<Peakrow projection={peakrowProjection} />

			<Boxed
				id={props['id']}
				projection={projection}
				data-testid={props['data-testid']}
			>
				{props.children}
			</Boxed>

			<SideBlinders />
		</Content>
	);
}

const Content = styled.div`
	margin: 0;
	width: 100vw;
	height: 100vh;
	padding-top: 0;
	padding-left: var(--offsetLeft);
`;

const Boxed = styled.div`
	margin: 0;
	padding: 0;
	overflow: hidden;
	width: var(--boxedWidth);
	height: var(--boxedHeight);
	background-image: url(${({ projection }) => projection.src});
	background-size: contain;
`;

const Peakrow = styled.div`
	margin: 0;
	padding: 0;
	overflow: hidden;
	margin-top: var(--blackHeight);
	width: var(--boxedWidth);
	${({ projection }) => (projection?.src ? 'height: var(--peakrowHeight)' : '')};
	${({ projection }) => (projection?.src ? `background-image: url(${projection.src})` : '')};
	background-repeat: no-repeat;
	background-size: var(--peakrowBackgroundSize);
	background-position-y: var(--peakrowBackgroundPosition);
`;

const ASPECT_RATIO = 16 / 9;

function handleResize({ size, peakrowProjection }, curWindowSize) {
	const boxed = computeAspectBoxedSize(ASPECT_RATIO, curWindowSize);

	const newScreenScale = boxed.width / size.width;

	const hasPeakrow = boxed.paddingTop > 0 && !!peakrowProjection?.src;
	const scaledOriginalPeakrowHeight = newScreenScale * peakrowProjection?.height;
	const topArea = boxed.paddingTop * 2;
	const peakrowBackgroundSize = scaledOriginalPeakrowHeight > boxed.paddingTop
		? 'cover'
		: 'contain';
	const peakrowHeight = hasPeakrow
		?
		topArea > scaledOriginalPeakrowHeight
			? scaledOriginalPeakrowHeight
			: topArea
		: 0;
	const totalSceneHeight = boxed.height + peakrowHeight;
	const blackHeight = totalSceneHeight < curWindowSize.height
		? parseInt((curWindowSize.height - totalSceneHeight) / 2)
		: 0;
	const offsetTop = peakrowHeight + blackHeight;
	const fixedTopContainerMarginTop = blackHeight > 0 && peakrowHeight > 0 ? blackHeight : 0;
	const peakrowBackgroundPosition = peakrowHeight - scaledOriginalPeakrowHeight;

	document.documentElement.style.setProperty('--screenScale', newScreenScale);

	document.documentElement.style.setProperty('--blackHeight', px(blackHeight));
	document.documentElement.style.setProperty('--offsetTop', px(offsetTop));
	document.documentElement.style.setProperty('--offsetLeft', px(boxed.paddingLeft));
	document.documentElement.style
		.setProperty('--fixedTopContainerMarginTop', px(fixedTopContainerMarginTop));

	document.documentElement.style.setProperty('--boxedWidth', px(boxed.width));
	document.documentElement.style.setProperty('--boxedHeight', px(boxed.height));

	document.documentElement.style.setProperty('--peakrowHeight', px(peakrowHeight));
	document.documentElement.style.setProperty('--peakrowBackgroundSize', peakrowBackgroundSize);
	document.documentElement
		.style.setProperty('--peakrowBackgroundPosition', px(peakrowBackgroundPosition));
};

function px(val) {
	return val && val !== 0 ? `${val}px` : '0px';
}

function fireResponsiveVariablesChangedEvent(newScreenScale) {
	document.dispatchEvent(creatScreenResponsivityChangeEvent(newScreenScale));
}

export default Backdrop;
